import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import {
  cCourseDevelopment, cCourseIstDevelopment, cSubmissionDevelopment, cRefreshCourseAnalysis, cRefreshSubmissionAnalysis,
} from "@/lib/api";
import { IstAcross, IstWithin } from "@/components/IstDevelopment";
import { CompositionCriteria } from "@/components/CompositionCriteria";


const ENTRY = "#B08968";   // muted clay — Initial trajectory
const FINAL = "#8C3A2A";   // brand — Developed trajectory
const btn = "px-4 py-2 rounded-sm text-sm font-medium transition-colors disabled:opacity-40";
const primary = `${btn} bg-[#8C3A2A] text-white hover:bg-[#6B2C20]`;
const ghost = `${btn} border border-stone-300 text-stone-700 hover:border-[#8C3A2A] hover:text-[#8C3A2A]`;

// Teacher-facing labels (frontend-only; does NOT touch WSR-1.3 keys/definitions or scoring).
const DIM_LABELS = {
  psd: { full: "Overall Structure (PSD)", table: "Overall" },
  orientation: { full: "Orientation", table: "Orientation" },
  organizing_structure: { full: "Organizing Structure", table: "Organization" },
  elaboration: { full: "Elaboration", table: "Elaboration" },
  grounding: { full: "Grounding", table: "Grounding" },
  closure: { full: "Closure", table: "Closure" },
  relational_coherence: { full: "Relational Coherence", table: "Coherence" },
};
const dimLabel = (key, kind = "full") => (DIM_LABELS[key]?.[kind] || key);
// Fixed row order for the composition details matrix: composite first, then coherence, then the essay parts
// in reading order (opening → closure). Display order only; does not change scoring.
const COMPOSITION_ROWS = ["psd", "relational_coherence", "orientation", "organizing_structure", "elaboration", "grounding", "closure"];

const fmt = (v) => (v === null || v === undefined ? "n/r" : v);
// Purely DESCRIPTIVE direction of observed change (no valence): a later lower score is data, not a decline.
const dirMark = (e, f) => {
  if (e === null || e === undefined || f === null || f === undefined) return "";
  if (f > e) return "↑";
  if (f < e) return "↓";
  return "→";
};

// Initial → Intermediate → Developed labels for a within-assignment revision sequence (display only).
const iterRoleLabel = (it, idx, total) => {
  if (it.iteration_role === "entry" || idx === 0) return "Initial";
  if (it.iteration_role === "final" || idx === total - 1) return "Developed";
  return `Revision ${idx}`;
};

function TrajectoryChart({ data, lines, testid }) {
  return (
    <div className="bg-white border border-stone-200 rounded-md p-4" data-testid={testid}>
      <ResponsiveContainer width="100%" height={340}>
        <LineChart data={data} margin={{ top: 10, right: 24, bottom: 8, left: -8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
          <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#78716c" }} />
          <YAxis domain={[0, 4]} ticks={[0, 1, 2, 3, 4]} tick={{ fontSize: 12, fill: "#78716c" }} />
          <Tooltip contentStyle={{ fontSize: 13, borderRadius: 6, border: "1px solid #e7e5e4" }}
            formatter={(v) => (v === null || v === undefined ? "—" : v)} />
          <Legend wrapperStyle={{ fontSize: 13 }} />
          {lines.map((l) => (
            <Line key={l.key} type="monotone" dataKey={l.key} name={l.name} stroke={l.color}
              strokeWidth={2.5} strokeDasharray={l.dashed ? "6 4" : undefined}
              dot={{ r: 4, fill: l.color }} activeDot={{ r: 6 }} connectNulls={false} />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// Collapsible score glossary. Concise, teacher-facing; does NOT reproduce the WSR-1.3 rubric or alter any scoring.
// Stable orientation for every per-student report: which student, Composition vs Thinking Structure,
// and Across vs Within — plus a clear one-level-back affordance. (SPEC: NAVIGATION.)
function StudentReportHeader({ studentName, report, onReport, context, assignmentTitle, onBackStudents, onBackAcross }) {
  const tab = (key, label, color) => (
    <button data-testid={`hdr-tab-${key}`} onClick={() => onReport && onReport(key)}
      className={`px-4 py-1.5 text-sm rounded-sm transition-colors ${report === key ? "text-white" : "text-stone-600 hover:text-stone-900"}`}
      style={report === key ? { background: color } : {}}>{label}</button>
  );
  return (
    <div className="mb-6" data-testid="student-report-header">
      <button className="text-[13px] text-stone-500 hover:text-stone-800" onClick={onBackStudents} data-testid="hdr-back-students">← Back to students</button>
      <h1 className="mt-2 font-serif text-3xl text-stone-900" data-testid="hdr-student-name">{studentName}</h1>
      <div className="mt-3 inline-flex items-center gap-1 bg-stone-100 border border-stone-200 rounded-md p-1" data-testid="hdr-report-tabs">
        {tab("composition", "Composition", "#8C3A2A")}
        {tab("thinking", "Thinking Structure", "#2F6F6A")}
      </div>
      <div className="mt-3 text-[12px] font-mono uppercase tracking-wider" data-testid="hdr-context">
        {context === "within" ? (
          <span className="text-stone-400">
            <button onClick={onBackAcross} className="hover:text-stone-700 hover:underline underline-offset-2" data-testid="hdr-back-across">Across assignments</button>
            <span className="mx-1.5">›</span>
            <span className="text-stone-600">Within: {assignmentTitle}</span>
          </span>
        ) : (
          <span className="text-stone-500">Across assignments</span>
        )}
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------
// SCREEN 1 — DEVELOPMENT / STUDENT CHOICE (grid).
// Students as ROWS, assignments as COLUMNS (1 → x). A Composition | Thinking Structure
// toggle governs the grid. Click a student's NAME for their full development (Across/Compare);
// click a CELL to open that one assignment directly. No scores on the primary face.
// --------------------------------------------------------------------------
export function CourseDevelopment({ courseId, report = "composition", onReportChange, onOpenStudent, onOpenCell, onBack }) {
  const [data, setData] = useState(null);
  const [ist, setIst] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [wsr, i] = await Promise.all([cCourseDevelopment(courseId), cCourseIstDevelopment(courseId).catch(() => null)]);
    setData(wsr); setIst(i);
  }, [courseId]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <p className="text-[13px] text-stone-400" data-testid="dev-loading">Loading development data…</p>;

  const { series, assignments } = data;
  const cols = [...assignments].sort((a, b) => a.order - b.order);
  const isThinking = report === "thinking";
  const accent = isThinking ? "#2F6F6A" : "#8C3A2A";
  const istPoint = (sid, order) => ist?.series?.find((x) => x.student_id === sid)?.points?.[order];

  const refresh = async () => {
    setBusy(true);
    try { await cRefreshCourseAnalysis(courseId); toast.success("Analysis started — results will appear as they complete."); setTimeout(load, 15000); }
    catch (e) { toast.error("Could not start analysis"); }
    finally { setBusy(false); }
  };

  const tab = (key, label, color) => (
    <button data-testid={`grid-report-${key}`} onClick={() => onReportChange && onReportChange(key)}
      className={`px-4 py-1.5 text-sm rounded-sm transition-colors ${report === key ? "text-white" : "text-stone-600 hover:text-stone-900"}`}
      style={report === key ? { background: color } : {}}>{label}</button>
  );

  return (
    <div data-testid="course-development">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl text-stone-900">Writing Development</h1>
          <p className="mt-2 text-[15px] text-stone-600 max-w-xl">Click a student's name to see their development, or a cell to open a single assignment.</p>
        </div>
        <button className="text-[12px] text-stone-400 hover:text-stone-700 shrink-0 mt-2" data-testid="dev-refresh-course" disabled={busy} onClick={refresh}>
          {busy ? "Starting…" : "Run analysis"}
        </button>
      </div>

      <div className="mt-6 inline-flex items-center gap-1 bg-stone-100 border border-stone-200 rounded-md p-1" data-testid="grid-report-tabs">
        {tab("composition", "Composition", "#8C3A2A")}
        {tab("thinking", "Thinking Structure", "#2F6F6A")}
      </div>

      <div className="mt-4 bg-white border border-stone-200 rounded-lg overflow-x-auto" data-testid="dev-grid">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="border-b border-stone-200 bg-stone-50">
              <th className="text-left px-4 py-3 sticky left-0 bg-stone-50 z-10 text-[12px] font-mono uppercase tracking-wider text-stone-400">Student</th>
              {cols.map((a, i) => (
                <th key={a.id} data-testid={`dev-grid-col-${a.id}`} className="px-3 py-3 text-center align-bottom min-w-[128px]">
                  <div className="text-[12px] font-medium text-stone-700">Assignment {i + 1}</div>
                  <div className="text-[11px] text-stone-400 truncate max-w-[168px] mx-auto" title={a.title}>{a.title.replace(/^Assignment\s*\d+\s*[—-]\s*/i, "")}</div>
                </th>
              ))}
              <th className="px-3 py-3 text-center align-bottom text-[11px] font-mono uppercase tracking-wider text-stone-400 min-w-[160px]">Across assignments</th>
            </tr>
          </thead>
          <tbody>
            {series.map((s) => (
              <tr key={s.student_id} data-testid={`dev-grid-row-${s.student_id}`} className="border-b border-stone-100 last:border-0">
                <td className="px-4 py-3 sticky left-0 bg-white z-10">
                  <button data-testid={`dev-grid-student-${s.student_id}`} onClick={() => onOpenStudent(s.student_id, s.student_name, report)}
                    className="font-serif text-lg text-stone-900 hover:underline underline-offset-4" style={{ textDecorationColor: accent }}>
                    {s.student_name}
                  </button>
                </td>
                {cols.map((a) => {
                  const p = s.points[a.order];
                  const hasSession = !!p?.session_id;
                  const analyzed = isThinking ? !!istPoint(s.student_id, a.order)?.analyzed : !!p?.analyzed;
                  return (
                    <td key={a.id} className="px-3 py-3 text-center">
                      {hasSession ? (
                        <button data-testid={`dev-cell-${s.student_id}-${a.id}`} className="group inline-flex w-full justify-center"
                          title={`Open ${isThinking ? "Thinking Structure" : "Composition"} — ${s.student_name} · ${a.title}`}
                          onClick={() => onOpenCell(s.student_id, s.student_name, p.session_id, a.title, report)}>
                          <span className="inline-flex items-center gap-1.5 rounded-full border border-stone-200 px-2.5 py-1 text-[12px] transition-colors group-hover:border-stone-400">
                            <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: analyzed ? accent : "transparent", border: `1.5px solid ${accent}` }} />
                            <span className="text-stone-500 group-hover:text-stone-800">{analyzed ? "Open" : "View"}</span>
                          </span>
                        </button>
                      ) : <span className="text-stone-300" data-testid={`dev-cell-empty-${s.student_id}-${a.id}`}>—</span>}
                    </td>
                  );
                })}
                <td className="px-3 py-3 text-center">
                  <button data-testid={`dev-open-development-${s.student_id}`} onClick={() => onOpenStudent(s.student_id, s.student_name, report)}
                    className="text-[12px] font-medium hover:underline whitespace-nowrap" style={{ color: accent }}>
                    See {isThinking ? "thinking" : "composition"} development →
                  </button>
                </td>
              </tr>
            ))}
            {!series.length && <tr><td colSpan={cols.length + 2} className="px-4 py-6 text-center text-[13px] text-stone-400">No students enrolled yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <p className="mt-3 text-[12px] text-stone-400 flex flex-wrap items-center gap-x-2 gap-y-1" data-testid="dev-grid-legend">
        <span className="inline-flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: accent }} /> analyzed</span>
        <span>·</span>
        <span className="inline-flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-full border" style={{ borderColor: accent }} /> submitted, not analyzed</span>
        <span>·</span>
        <span>— not started</span>
      </p>

      <button className={ghost + " mt-8"} data-testid="dev-back" onClick={onBack}>← Back to course</button>
    </div>
  );
}

// --------------------------------------------------------------------------
// SCREEN 2 — COMPOSITION / THINKING · ACROSS ASSIGNMENTS (per student).
// Composition: one dominant Initial-vs-Developed graph + clickable assignments; dimensions behind disclosure.
// Thinking: defers entirely to IstAcross (maps-first).
// --------------------------------------------------------------------------
export function StudentDevelopment({ courseId, studentId, studentName, report = "composition", onReport, onOpenAssignment, onBack }) {
  const [data, setData] = useState(null);
  const [details, setDetails] = useState(false);

  const load = useCallback(async () => { setData(await cCourseDevelopment(courseId)); }, [courseId]);
  useEffect(() => { load(); }, [load]);

  const header = (
    <StudentReportHeader studentName={studentName} report={report} onReport={onReport} context="across" onBackStudents={onBack} />
  );

  if (!data) return <div data-testid="student-development">{header}<p className="text-[13px] text-stone-400">Loading…</p></div>;
  const { assignments } = data;
  const student = data.series.find((s) => s.student_id === studentId);
  if (!student) return <div data-testid="student-development">{header}<p className="text-[13px] text-stone-400">No data for this student.</p></div>;

  if (report === "thinking") {
    return (
      <div data-testid="student-development">
        {header}
        <div data-testid="report-thinking">
          <IstAcross courseId={courseId} studentId={studentId} onOpenAssignment={onOpenAssignment} />
        </div>
      </div>
    );
  }

  const cols = [...assignments].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const chartData = cols.map((a) => ({
    name: a.title.length > 16 ? a.title.slice(0, 15) + "…" : a.title,
    entry: student.points[a.order]?.entry?.psd ?? null,
    final: student.points[a.order]?.final?.psd ?? null,
  }));
  const hasScores = student.points.some((p) => p.analyzed);

  return (
    <div data-testid="student-development">
      {header}
      <div data-testid="report-composition">
        <h2 className="font-serif text-2xl text-stone-900">Composition development</h2>
        <p className="mt-1 mb-4 text-[13px] text-stone-500">Overall composition (PSD) — <span className="font-medium text-stone-700">Initial</span> vs <span className="font-medium text-stone-700">Developed</span> across assignments. Click an assignment to see how it changed through revision.</p>
        {hasScores ? (
          <TrajectoryChart testid="student-trajectory-chart" data={chartData}
            lines={[{ key: "entry", name: "Initial", color: ENTRY, dashed: true }, { key: "final", name: "Developed", color: FINAL }]} />
        ) : (
          <div className="bg-white border border-dashed border-stone-300 rounded-md p-8 text-center" data-testid="student-no-scores">
            <p className="text-sm text-stone-500">No composition scores yet for this student.</p>
          </div>
        )}
        <div className="mt-4 space-y-2" data-testid="composition-assignment-list">
          {cols.map((a) => {
            const p = student.points[a.order];
            return (
              <div key={a.id} className="bg-white border border-stone-200 rounded-md p-4 flex items-center justify-between hover:border-[#8C3A2A] transition-colors">
                <span className="text-stone-800 text-sm font-medium">{a.title}</span>
                {p?.session_id ? <button className={ghost} data-testid={`open-assignment-dev-${a.id}`} onClick={() => onOpenAssignment(p.session_id, a.title)}>Open →</button> : <span className="text-[12px] text-stone-400">Not started</span>}
              </div>
            );
          })}
        </div>
        <button onClick={() => setDetails(!details)} data-testid="explore-composition-details" className="mt-5 text-[13px] font-medium text-[#8C3A2A] hover:underline">
          {details ? "Hide composition details" : "Explore composition details →"}
        </button>
        <CompositionCriteria />
        {details && (
          <div className="mt-3" data-testid="composition-details">
            <div className="bg-white border border-stone-200 rounded-md overflow-x-auto" data-testid="student-profile-matrix">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-stone-200 bg-stone-50">
                  <th className="text-left px-4 py-2 text-[12px] font-mono uppercase tracking-wider text-stone-400">Part of the essay</th>
                  {cols.map((a, i) => <th key={a.id} className="text-center px-3 py-2 text-[12px] text-stone-600 font-medium" title={a.title}>Assignment {i + 1}</th>)}
                </tr></thead>
                <tbody>
                  {COMPOSITION_ROWS.map((key) => (
                    <tr key={key} className="border-b border-stone-100 last:border-0" data-testid={`profile-row-${key}`}>
                      <td className={`px-4 py-2 ${key === "psd" ? "font-semibold text-stone-800" : "text-stone-700"}`}>{dimLabel(key, "full")}</td>
                      {cols.map((a) => {
                        const p = student.points[a.order]; const e = p?.entry?.[key], f = p?.final?.[key];
                        if (!p?.analyzed) return <td key={a.id} className="text-center px-3 py-2 text-stone-300">·</td>;
                        return <td key={a.id} className={`text-center px-3 py-2 font-mono ${key === "psd" ? "text-stone-900 font-semibold" : "text-stone-700"}`}>{fmt(e)} → {fmt(f)} <span className="text-stone-400">{dirMark(e, f)}</span></td>;
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------
// SCREEN 3 / 5 — WITHIN ONE ASSIGNMENT.
// Composition: prominent Initial → Intermediate → Developed PSD trajectory; dimensions/evidence behind disclosure.
// Thinking: defers to IstWithin (large maps + plain language primary).
// --------------------------------------------------------------------------
export function AssignmentDevelopment({ report = "composition", sessionId, assignmentTitle, courseId, studentId, studentName, onReport, onOpenAssignment, onBackToStudent, onBackList }) {
  const [data, setData] = useState(null);
  const [siblings, setSiblings] = useState(null);
  const [details, setDetails] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => { setData(await cSubmissionDevelopment(sessionId)); }, [sessionId]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!courseId || !studentId) { setSiblings({ items: [] }); return; }
    cCourseDevelopment(courseId).then((cd) => {
      const st = cd.series.find((s) => s.student_id === studentId);
      const items = st ? st.points.filter((p) => p.session_id).map((p) => ({ session_id: p.session_id, title: p.assignment_title })) : [];
      setSiblings({ items });
    }).catch(() => setSiblings({ items: [] }));
  }, [courseId, studentId]);

  const who = studentName || data?.student?.name || "this student";
  const showThinking = report === "thinking";

  const header = (
    <StudentReportHeader studentName={who} report={report} onReport={onReport} context="within"
      assignmentTitle={assignmentTitle || data?.assignment?.title}
      onBackStudents={onBackList} onBackAcross={onBackToStudent} />
  );

  const idx = siblings ? siblings.items.findIndex((x) => x.session_id === sessionId) : -1;
  const prev = idx > 0 ? siblings.items[idx - 1] : null;
  const next = idx >= 0 && idx < siblings.items.length - 1 ? siblings.items[idx + 1] : null;
  const continueNav = (
    <div className="mt-8 border-t border-stone-200 pt-6" data-testid="history-continue-nav">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-[140px]">
          {prev && <button className={ghost} data-testid="history-prev" onClick={() => onOpenAssignment(prev.session_id, prev.title)}>← Previous assignment</button>}
        </div>
        <button className={primary} data-testid="history-back-student" onClick={onBackToStudent}>Back to {who}'s development</button>
        <div className="flex-1 min-w-[140px] flex justify-end">
          {next && <button className={ghost} data-testid="history-next" onClick={() => onOpenAssignment(next.session_id, next.title)}>Next assignment →</button>}
        </div>
      </div>
    </div>
  );

  if (!data) return <div data-testid="assignment-development">{header}<p className="text-[13px] text-stone-400">Loading…</p></div>;

  if (showThinking) {
    return (
      <div data-testid="assignment-development">
        {header}
        <IstWithin sessionId={sessionId} />
        {continueNav}
      </div>
    );
  }

  // Composition · Within
  const { iterations, dimensions } = data;
  const total = iterations.length;
  const chartData = iterations.map((it, i) => ({ name: iterRoleLabel(it, i, total), value: it.analyzed ? (it.scores?.psd ?? null) : null }));
  const hasScores = iterations.some((it) => it.analyzed);
  const missing = data.iteration_count - data.analyzed_count;

  const refresh = async () => {
    setBusy(true);
    try { await cRefreshSubmissionAnalysis(sessionId); toast.success("Analyzing full revision history — this runs in the background."); setTimeout(load, 15000); }
    catch (e) { toast.error("Could not start analysis"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="assignment-development">
      {header}
      <h2 className="font-serif text-2xl text-stone-900">How this piece developed through revision</h2>
      <p className="mt-1 mb-4 text-[13px] text-stone-500">Overall composition (PSD) from <span className="font-medium text-stone-700">Initial</span> through revisions to <span className="font-medium text-stone-700">Developed</span>.</p>

      {hasScores ? (
        <div className="bg-white border border-stone-200 rounded-md p-4" data-testid="assignment-sequence-chart">
          <ResponsiveContainer width="100%" height={340}>
            <LineChart data={chartData} margin={{ top: 10, right: 24, bottom: 8, left: -8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
              <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#78716c" }} />
              <YAxis domain={[0, 4]} ticks={[0, 1, 2, 3, 4]} tick={{ fontSize: 12, fill: "#78716c" }} />
              <Tooltip contentStyle={{ fontSize: 13, borderRadius: 6, border: "1px solid #e7e5e4" }}
                formatter={(v) => (v === null || v === undefined ? "—" : v)} />
              <Line type="monotone" dataKey="value" name="Overall Structure (PSD)" stroke={FINAL} strokeWidth={2.5}
                dot={{ r: 5, fill: FINAL }} activeDot={{ r: 7 }} connectNulls={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="bg-white border border-dashed border-stone-300 rounded-md p-8 text-center" data-testid="assignment-no-scores">
          <p className="text-sm text-stone-500">No developmental scores yet for this submission.</p>
        </div>
      )}

      {missing > 0 && (
        <p className="mt-3 text-[12px] text-stone-400" data-testid="assignment-dev-gap-note">
          {missing} intermediate version{missing > 1 ? "s" : ""} not yet analyzed.{" "}
          <button onClick={refresh} disabled={busy} className="underline hover:text-stone-700" data-testid="assignment-dev-refresh">{busy ? "starting…" : "Analyze full history"}</button>
        </p>
      )}

      <button onClick={() => setDetails(!details)} data-testid="explore-composition-details" className="mt-5 text-[13px] font-medium text-[#8C3A2A] hover:underline">
        {details ? "Hide composition details" : "Explore composition details →"}
      </button>
      <CompositionCriteria />
      {details && (
        <div className="mt-3" data-testid="composition-details">
          <div className="bg-white border border-stone-200 rounded-md overflow-x-auto" data-testid="assignment-iteration-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-stone-200 bg-stone-50">
                  <th className="text-left px-4 py-2 text-[12px] font-mono uppercase tracking-wider text-stone-400">Version</th>
                  {dimensions.map((d) => (
                    <th key={d.key} className="text-center px-2 py-2 text-[11px] text-stone-500 whitespace-nowrap" title={dimLabel(d.key, "full")}>{dimLabel(d.key, "table")}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {iterations.map((it, i) => (
                  <tr key={it.iteration_number} className="border-b border-stone-100 last:border-0">
                    <td className="px-4 py-2 text-stone-700">#{it.iteration_number} <span className="text-[11px] text-stone-400">{iterRoleLabel(it, i, total)}</span></td>
                    {dimensions.map((d) => (
                      <td key={d.key} className="text-center px-2 py-2 font-mono text-stone-600">{it.analyzed ? fmt(it.scores?.[d.key]) : "·"}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {continueNav}
    </div>
  );
}
